import Image from "../../components/image";
import { ArrowRight } from "lucide-react";
import { ImageReveal } from "../../components/image-reveal";
import { Stagger, StaggerItem } from "../../components/motion";
import { CTA, PageHero, SectionHeading } from "../../components/site-shell";
import { aboutValues } from "../../lib/site-data";
import { createMetadata } from "../../lib/seo";

export const metadata = createMetadata({
  title: "关于我们｜品牌增长与出海团队",
  description:
    "了解增长深度 GrowDeep：以 AI 技术和专业团队为企业提供品牌增长、产品出海、官网、内容、SEO/GEO、线索与持续运营服务。",
  path: "/about",
  keywords: [
    "增长深度",
    "GrowDeep",
    "品牌增长公司",
    "产品出海服务商",
    "企业出海服务公司",
    "制造业出海服务商",
    "企业官网建设公司",
    "海外独立站服务商",
    "SEO优化服务商",
    "GEO优化服务商",
    "AI搜索优化公司",
    "企业内容营销团队",
    "深圳品牌营销公司",
    "深圳数字营销公司",
    "企业增长团队",
  ],
  image: "/assets/office.jpg",
});

const companyFacts = [
  ["香港 · 杭州 · 深圳", "多地协同，覆盖技术、品牌与市场增长服务"],
  ["AI 工具 + 专业团队", "工具提升效率，团队对策略判断与交付质量负责"],
  ["6 年", "核心团队持续协作，覆盖产品、技术、运营与商业化"],
];

const businessPaths = [
  {
    number: "01",
    title: "企业品牌增长",
    description:
      "帮助企业把品牌价值讲清楚，把官网与内容建设成可持续的市场阵地。",
    items: ["品牌与产品表达", "官网与内容资产", "SEO / GEO 与咨询入口"],
    image: "/assets/city.jpg",
  },
  {
    number: "02",
    title: "产品出海增长",
    description:
      "围绕目标市场建立产品被发现、被理解、被信任并产生询盘的增长体系。",
    items: [
      "目标市场与国际化表达",
      "海外独立站与搜索内容",
      "数字营销与询盘承接",
    ],
    image: "/assets/earth.jpg",
  },
];

const collaborationSteps = [
  ["01", "增长诊断", "确认业务目标、关键问题与阶段优先级。"],
  ["02", "策略共识", "明确范围、资源、协作边界与成功标准。"],
  ["03", "建设交付", "形成品牌、网站、内容与搜索等增长资产。"],
  ["04", "上线验证", "检查访问、咨询、数据与基础表现，发现真实问题。"],
  ["05", "持续运营", "按周期更新、测试、承接线索并复盘下一步。"],
];

const accountability = [
  ["有目标", "项目开始前明确业务目标、目标客户、核心市场与阶段成功标准。"],
  ["有负责人", "项目负责人、交付负责人、客户对接人与确认节点清晰可查。"],
  ["有记录", "页面、内容、发布、数据、线索与阶段结论持续沉淀。"],
  ["有复盘", "结合真实数据、搜索变化与客户反馈，调整下一阶段动作。"],
];

export default function AboutPage() {
  return (
    <>
      <PageHero
        eyebrow="About GrowDeep"
        title="关于增长深度 GrowDeep"
        description="增长深度 GrowDeep 是蔚灵深度科技旗下企业增长服务品牌。我们以 AI 技术提升协作效率，以专业团队帮助企业建立长期掌握的品牌与客户增长系统。"
        image="/assets/office.jpg"
      />

      <section className="section-shell section-block">
        <div className="about-profile">
          <div className="about-profile-copy">
            <span className="eyebrow">Company profile</span>
            <h2>用技术加速增长工作，用专业判断把增长做对。</h2>
            <p>
              蔚灵深度科技是一家以 AI
              技术驱动企业品牌建设与市场增长的科技公司，已在香港、杭州和深圳形成多地协同布局。增长深度面向国内品牌增长与产品出海增长，将品牌、网站、内容、搜索、线索与运营连接为完整路径。
            </p>
          </div>
          <dl className="about-facts">
            {companyFacts.map(([value, label]) => (
              <div key={value}>
                <dt>{value}</dt>
                <dd>{label}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      <section id="team" className="section-shell section-block">
        <div className="about-team-layout">
          <ImageReveal className="about-team-image" mode="rise">
            <Image
              src="/assets/team.jpg"
              alt="增长深度跨职能团队"
              fill
              sizes="(max-width: 900px) 100vw, 48vw"
            />
          </ImageReveal>
          <div className="about-team-copy">
            <span className="eyebrow">People and technology</span>
            <h2>AI 增强效率，专业团队对策略判断与交付质量负责。</h2>
            <p>
              AI
              用于加速资料整理、内容协作、页面制作、搜索研究与数据汇总；策略、优先级、内容质量、项目推进与最终责任，始终由专业成员协同把关。
            </p>
            <div className="about-value-grid">
              {aboutValues.map(({ icon: Icon, title, text }) => (
                <div className="about-value-item" key={title}>
                  <Icon />
                  <h3>{title}</h3>
                  <p>{text}</p>
                </div>
              ))}
            </div>
            <dl className="about-leadership">
              <div>
                <dt>袁琳</dt>
                <dd>创始人兼 CEO，负责战略、产品与全球市场。</dd>
              </div>
              <div>
                <dt>张伟</dt>
                <dd>联合创始人兼 CTO，负责架构与研发。</dd>
              </div>
              <div>
                <dt>林曦</dt>
                <dd>合伙人兼 COO，负责运营与用户增长。</dd>
              </div>
            </dl>
          </div>
        </div>
      </section>

      <section id="method" className="section-muted section-block">
        <div className="section-shell">
          <SectionHeading
            eyebrow="How we work"
            title="先验证，再共创，再持续运营。"
            description="不从模板和服务清单出发，而是先识别真正影响增长的问题；关键策略、上线确认、变更与验收均保留可查看的交付记录。"
          />
          <Stagger className="about-collaboration-grid">
            {collaborationSteps.map(([number, title, text]) => (
              <StaggerItem className="about-collaboration-step" key={number}>
                <span>{number}</span>
                <h3>{title}</h3>
                <p>{text}</p>
              </StaggerItem>
            ))}
          </Stagger>
          <div className="about-review-loop">
            <div>
              <h3>每月不只汇报做了多少，更判断什么正在带来真实变化。</h3>
              <p>围绕搜索曝光、内容资产、咨询点击与 AI 可见度观察趋势。</p>
            </div>
            <div className="about-review-questions" aria-label="月度复盘问题">
              <span>发生了什么</span>
              <ArrowRight size={16} aria-hidden="true" />
              <span>为什么发生</span>
              <ArrowRight size={16} aria-hidden="true" />
              <span>下一步做什么</span>
            </div>
          </div>
        </div>
      </section>

      <section className="section-shell section-block-sm">
        <SectionHeading
          eyebrow="Accountability"
          title="对过程负责，也让每一项增长资产持续发挥作用。"
          description="我们不承诺无法控制的结果，但会让每一步增长动作可见、可判断、可追踪，并在数据与反馈中持续优化。"
        />
        <Stagger className="about-accountability-grid">
          {accountability.map(([title, text], index) => (
            <StaggerItem className="about-accountability-item" key={title}>
              <span>0{index + 1}</span>
              <h3>{title}</h3>
              <p>{text}</p>
            </StaggerItem>
          ))}
        </Stagger>
      </section>

      <CTA />
    </>
  );
}
